"use client";

import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency, formatMinutes } from "@/lib/format";
import { Clock, Car, DollarSign, ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface PeriodSummaryProps {
  period: 'daily' | 'weekly' | 'monthly';
  stats: {
    blockCount: number;
    totalEarnings: number;
    totalMinutes: number;
    actualMinutes?: number;
    totalMiles: number;
    avgHourlyRate: number;
    actualHourlyRate?: number;
    avgPerMileRate: number;
  } | null;
  fuelEstimate?: {
    fuelCost: number;
    mpg: number;
    fuelPrice: number;
  } | null;
}

export function PeriodSummary({ period, stats, fuelEstimate }: PeriodSummaryProps) {
  if (!stats) {
    return <PeriodSummarySkeleton />;
  }

  if (stats.blockCount === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No blocks {period === 'daily' ? 'on this day' : period === 'weekly' ? 'this week' : 'this month'}
        </CardContent>
      </Card>
    );
  }

  const hasFuel = fuelEstimate && fuelEstimate.fuelCost > 0 && stats.totalMiles > 0;
  const netProfit = hasFuel ? stats.totalEarnings - fuelEstimate.fuelCost : stats.totalEarnings;
  const displayRate = stats.actualHourlyRate ?? stats.avgHourlyRate;
  const displayMinutes = stats.actualMinutes ?? stats.totalMinutes;

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        {/* Primary: Earnings with optional fuel/net */}
        {hasFuel ? (
          <div className="flex items-center justify-center gap-4 py-2">
            <div className="text-center">
              <p className="text-2xl font-bold">{formatCurrency(stats.totalEarnings)}</p>
              <p className="text-xs text-muted-foreground">gross</p>
            </div>
            <div className="flex flex-col items-center">
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-destructive">-{formatCurrency(fuelEstimate.fuelCost)}</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                {formatCurrency(netProfit)}
              </p>
              <p className="text-xs text-muted-foreground">net profit</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-2">
            <p className="text-3xl font-bold">{formatCurrency(stats.totalEarnings)}</p>
            <p className="text-xs text-muted-foreground">total earnings</p>
          </div>
        )}

        {/* Secondary: Block count, duration, miles */}
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground border-t pt-3">
          <span>{stats.blockCount} block{stats.blockCount !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {formatMinutes(displayMinutes)}
            {stats.actualMinutes && stats.actualMinutes !== stats.totalMinutes && (
              <span className="text-xs">({formatMinutes(stats.totalMinutes)} booked)</span>
            )}
          </span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <Car className="h-3.5 w-3.5" />
            {stats.totalMiles.toFixed(1)} mi
          </span>
        </div>

        {/* Tertiary: Rates */}
        <div className="flex justify-between text-sm border-t pt-3">
          <div className="flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{formatCurrency(Math.round(displayRate))}/hr</span>
            {stats.actualHourlyRate && (
              <span className="text-xs text-muted-foreground">actual</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Car className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">
              {formatCurrency(Math.round(stats.avgPerMileRate * 100) / 100)}/mi
            </span>
          </div>
        </div>

        {/* Fuel disclaimer */}
        {hasFuel && (
          <p className="text-xs text-muted-foreground text-center">
            Est. fuel: {fuelEstimate.mpg} MPG @ {formatCurrency(fuelEstimate.fuelPrice)}/gal
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PeriodSummarySkeleton() {
  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="text-center py-2">
          <Skeleton className="h-10 w-32 mx-auto mb-1" />
          <Skeleton className="h-3 w-20 mx-auto" />
        </div>
        <div className="flex justify-center gap-4 border-t pt-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex justify-between border-t pt-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
        </div>
      </CardContent>
    </Card>
  );
}
